/**
 * The idle-warning chime — slice-3560 (ADR-0046).
 *
 * <p>PB calls `beep(1)` on **every** timer tick for the whole 60-second countdown
 * (`w_phfc_timer_countdown.srw:70`). That is faithful and it is also, in an office, genuinely
 * unpleasant — so PH10 deliberately does not copy the cadence: one chime when the modal opens
 * and one at T-10s (design doc §5.2, Q2).
 *
 * <p>Synthesised with an inline `AudioContext` rather than an audio asset: the artifact/CSP
 * rules make external media awkward, and a two-oscillator blip is a few lines. It is
 * best-effort by construction — browsers block audio until the page has had a user gesture,
 * and `AudioContext` does not exist in jsdom at all — so every path is wrapped and a failure
 * is silent. The chime is never the only signal; the modal is.
 */

type AudioContextCtor = new () => AudioContext;

function audioContextCtor(): AudioContextCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    AudioContext?: AudioContextCtor;
    webkitAudioContext?: AudioContextCtor;
  };
  return w.AudioContext ?? w.webkitAudioContext ?? null;
}

let ctx: AudioContext | null = null;

/**
 * Play one short two-tone chime. Silent — and cheap — when the tab is hidden (nobody is there
 * to hear it, and a background tab's audio is startling when it is not) or when the tenant has
 * `Session.WarnSound = N`; the caller applies the config gate, this one applies the tab gate.
 */
export function playWarningChime(): void {
  try {
    if (typeof document !== 'undefined' && document.hidden) return;
    const Ctor = audioContextCtor();
    if (!Ctor) return;
    if (!ctx) ctx = new Ctor();
    // A context created before any user gesture starts suspended; resume() is a promise that
    // rejects on browsers that refuse. Nothing to do about it — swallow and stay silent.
    void ctx.resume?.().catch(() => {});

    const start = ctx.currentTime;
    // Two rising blips, ~90ms each, at a comfortable indoor level. Short enough not to talk
    // over anyone, distinct enough to notice across a room.
    [
      { at: 0, hz: 880 },
      { at: 0.13, hz: 1174 },
    ].forEach(({ at, hz }) => {
      const osc = ctx!.createOscillator();
      const gain = ctx!.createGain();
      osc.type = 'sine';
      osc.frequency.value = hz;
      gain.gain.setValueAtTime(0.0001, start + at);
      gain.gain.exponentialRampToValueAtTime(0.12, start + at + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + at + 0.09);
      osc.connect(gain).connect(ctx!.destination);
      osc.start(start + at);
      osc.stop(start + at + 0.1);
    });
  } catch {
    // Audio is a nicety. Never let it break the warning it accompanies.
  }
}

/** Test seam — drops the cached context so a suite can assert construction counts. */
export function resetChimeForTests(): void {
  try {
    void ctx?.close?.();
  } catch {
    /* ignore */
  }
  ctx = null;
}
